import {
  Button,
  ColumnHelp,
  DateRangePicker,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  TOUCH,
  ToggleGroup,
  ToggleGroupItem,
  useDialogDirty,
} from "@nymbl/nos2-rules";
// src/components/pto/RequestDialog.tsx
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { toast } from "sonner";
import { invalidatePtoWrite, useCreateRequest, useQuote, useUpdateRequest } from "@/hooks/use-pto";
import { PTO_DAY_LENGTH_LABELS, PTO_DAY_LENGTHS, type PtoDayLength } from "@/lib/enums";
import { type BalanceRow, selectableTypes, typeHelp } from "@/lib/pto/balances";
import {
  PTO_FULL_DAY_HOURS,
  PTO_HALF_DAY_HOURS,
  PTO_HOUR_OPTIONS,
  PTO_NOTE_MAX,
} from "@/lib/pto/constants";
import { typeLabel } from "@/lib/pto/days";
import { type MyRequest, ownHolidayDates } from "@/lib/pto/my-requests";
import { quoteLine } from "@/lib/pto/quote";
import {
  type CreateValues,
  guardClose,
  heldCreate,
  settleOnOpen,
  settleOnSubmit,
} from "@/lib/pto/uncertain-create";
import { ptoCreateCheckKeys, ptoWriteKeys } from "@/lib/query";
import { newId } from "@/lib/time/new-id";
import { cn } from "@/lib/utils";
import type { MyRequestsView, QuoteQuery } from "@/schemas/pto";

interface RequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  balances: BalanceRow[];
  requests: MyRequestsView | undefined;
  /** A pending, rejected or draft request to edit; null for a new one. */
  editing: MyRequest | null;
}

function lengthOf(hours: number): PtoDayLength {
  if (hours === PTO_FULL_DAY_HOURS) return "full";
  if (hours === PTO_HALF_DAY_HOURS) return "half";
  return "hours";
}

function hoursOf(length: PtoDayLength, custom: number): number {
  if (length === "full") return PTO_FULL_DAY_HOURS;
  if (length === "half") return PTO_HALF_DAY_HOURS;
  return custom;
}

interface RequestFormProps extends Omit<RequestDialogProps, "open" | "onOpenChange"> {
  /** The new request's browser-made id; the same one on every retry of the same fields. */
  newRequestId: string;
  /** The fields a timed-out create was sent with, restored so its retry is the same request. */
  initial?: CreateValues;
  /** The timed-out create had landed and the fields changed: start an empty form. */
  onFresh: () => void;
  onDone: () => void;
}

const SAVED = "Request sent";

function RequestForm({
  balances,
  requests,
  editing,
  newRequestId,
  initial,
  onFresh,
  onDone,
}: RequestFormProps) {
  const qc = useQueryClient();
  const types = selectableTypes(balances);
  const start = editing
    ? {
        typeId: editing.typeId,
        startDate: editing.startDate,
        endDate: editing.endDate,
        hoursPerDay: editing.hoursPerDay,
        description: editing.description ?? "",
      }
    : initial;
  const [requestId, setRequestId] = useState(newRequestId);
  const [typeId, setTypeId] = useState(start?.typeId ?? types[0]?.typeId ?? "");
  const [from, setFrom] = useState<string | null>(start?.startDate ?? null);
  const [to, setTo] = useState<string | null>(start?.endDate ?? null);
  const [length, setLength] = useState<PtoDayLength>(start ? lengthOf(start.hoursPerDay) : "full");
  const [custom, setCustom] = useState(
    start && lengthOf(start.hoursPerDay) === "hours" ? start.hoursPerDay : 2,
  );
  const [note, setNote] = useState(start?.description ?? "");
  const create = useCreateRequest();
  const update = useUpdateRequest();
  useDialogDirty(
    typeId !== (start?.typeId ?? types[0]?.typeId ?? "") ||
      from !== (start?.startDate ?? null) ||
      to !== (start?.endDate ?? null) ||
      note !== (start?.description ?? ""),
  );
  // A create whose last send timed out is read before it is sent again (never the same id with other fields).
  const settle = useMutation({
    mutationKey: ptoWriteKeys.request(),
    retry: false,
    mutationFn: ({ id, values }: { id: string; values: CreateValues }) =>
      settleOnSubmit(id, values),
    onError: () => toast.error("Something went wrong. Try again."),
  });

  const hours = hoursOf(length, custom);
  const editingId = editing?.id;
  // Memoized on primitives, so the quote's key only changes when an input does.
  const query = useMemo<QuoteQuery | null>(
    () =>
      typeId && from && to
        ? { typeId, start: from, end: to, hours, ...(editingId ? { excludeId: editingId } : {}) }
        : null,
    [typeId, from, to, hours, editingId],
  );
  const quote = useQuote(query);
  const holidays = useMemo(() => ownHolidayDates(requests?.rows ?? []), [requests]);
  const closed = useMemo(() => new Set(requests?.closedMonths ?? []), [requests]);
  const selected = types.find((t) => t.typeId === typeId);
  const help = selected ? typeHelp(selected) : null;
  const pending = create.isPending || update.isPending || settle.isPending;
  const line = quoteLine({
    asked: query !== null,
    hasData: quote.data !== undefined,
    isError: quote.isError,
    isFetching: quote.isFetching,
  });
  const ready = line === "shown" && !quote.isFetching && quote.data?.blocked === false;

  function submit() {
    if (!from || !to || !typeId) return;
    const body = { typeId, startDate: from, endDate: to, hoursPerDay: hours, description: note };
    if (editing) {
      update.mutate(
        { ...body, id: editing.id, changedOn: editing.changedOn },
        { onSuccess: onDone },
      );
      return;
    }
    settle.mutate(
      { id: requestId, values: body },
      {
        onSuccess: (out) => {
          if (out.kind === "saved") {
            toast.success(SAVED);
            invalidatePtoWrite(qc);
            if (out.same) onDone();
            else onFresh();
            return;
          }
          const id = out.kind === "send" ? out.id : newId();
          if (id !== requestId) setRequestId(id);
          // The send holds and releases the id itself (it must survive this form unmounting).
          create.mutate({ ...body, id }, { onSuccess: onDone });
        },
      },
    );
  }

  const submitLabel = !editing ? "Submit" : editing.status === "rejected" ? "Resubmit" : "Save";

  return (
    <form
      className="flex min-h-0 flex-1 flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        if (ready && !pending) submit();
      }}
    >
      <DialogBody className="flex flex-col gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <Label htmlFor="pto-type">Type</Label>
            {help && <ColumnHelp name="Type" text={help} />}
          </div>
          <Select value={typeId} onValueChange={setTypeId}>
            <SelectTrigger id="pto-type" className="w-full">
              <SelectValue placeholder="Pick a type" />
            </SelectTrigger>
            <SelectContent>
              {types.map((t) => (
                <SelectItem key={t.typeId} value={t.typeId}>
                  {typeLabel(t.typeEnum, t.typeName)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pto-dates">Dates</Label>
          <DateRangePicker
            id="pto-dates"
            from={from}
            to={to}
            marked={holidays}
            isDisabled={(iso) => closed.has(`${iso.slice(0, 7)}-01`)}
            onChange={(r) => {
              setFrom(r.from);
              setTo(r.to ?? r.from);
            }}
          />
        </div>

        <div className="space-y-2">
          <Label id="pto-length-label">Day length</Label>
          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              type="single"
              variant="outline"
              aria-labelledby="pto-length-label"
              value={length}
              onValueChange={(v) => {
                const next = PTO_DAY_LENGTHS.find((l) => l === v);
                if (next) setLength(next);
              }}
            >
              {PTO_DAY_LENGTHS.map((l) => (
                <ToggleGroupItem key={l} value={l} className={TOUCH}>
                  {PTO_DAY_LENGTH_LABELS[l]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            {length === "hours" && (
              <Select value={String(custom)} onValueChange={(v) => setCustom(Number(v))}>
                <SelectTrigger className="w-28" aria-label="Hours per day">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PTO_HOUR_OPTIONS.map((h) => (
                    <SelectItem key={h} value={String(h)}>
                      {h} hrs
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="pto-note">Note</Label>
          <Input
            id="pto-note"
            value={note}
            maxLength={PTO_NOTE_MAX}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div
          className="flex min-h-11 flex-wrap items-center justify-between gap-2 border-t pt-3"
          aria-live="polite"
        >
          {line === "loading" && <Skeleton className="h-5 w-48" />}
          {line === "error" && (
            <>
              <span className="flex items-center gap-1.5 text-sm text-danger">
                <AlertCircle className="size-4" aria-hidden="true" />
                Couldn't work out the days
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={TOUCH}
                onClick={() => void quote.refetch()}
              >
                Retry
              </Button>
            </>
          )}
          {line === "shown" && quote.data && (
            <span className="text-sm font-semibold">{quote.data.summary}</span>
          )}
          {line === "shown" && quote.data?.chip && (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-semibold",
                quote.data.chip.tone === "danger"
                  ? "bg-danger-bg text-danger"
                  : "bg-muted text-muted-foreground",
              )}
            >
              {quote.data.chip.text}
            </span>
          )}
        </div>
      </DialogBody>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          className={TOUCH}
          disabled={pending}
          onClick={onDone}
        >
          Cancel
        </Button>
        <Button type="submit" className={TOUCH} disabled={!ready || pending}>
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

type NewFormProps = Omit<RequestFormProps, "editing" | "newRequestId" | "initial" | "onFresh">;

/**
 * A new request. Mounted on every open (the dialog's content unmounts when closed), so a create
 * that timed out is settled first: landed means saved and a fresh form; not landed restores its
 * id and fields.
 */
function NewRequestForm(props: NewFormProps) {
  const qc = useQueryClient();
  const [heldAtOpen] = useState(heldCreate);
  const [fresh, setFresh] = useState(() => ({ n: 0, id: newId() }));
  const check = useQuery({
    queryKey: ptoCreateCheckKeys.check(heldAtOpen?.id ?? ""),
    queryFn: async () => {
      const out = await settleOnOpen();
      if (out.kind === "saved") {
        toast.success(SAVED);
        invalidatePtoWrite(qc);
      }
      return out;
    },
    enabled: heldAtOpen !== null,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    retry: false,
  });
  if (heldAtOpen && check.isPending) {
    return (
      <DialogBody>
        <Skeleton className="h-72 w-full" />
      </DialogBody>
    );
  }
  const restore = fresh.n === 0 && check.data?.kind === "restore" ? check.data : null;
  return (
    <RequestForm
      {...props}
      key={restore ? `restore-${restore.id}` : `new-${fresh.n}`}
      editing={null}
      newRequestId={restore?.id ?? fresh.id}
      initial={restore?.values}
      onFresh={() => setFresh((f) => ({ n: f.n + 1, id: newId() }))}
    />
  );
}

/** Request time off (spec 1.4); fills the screen on phones (spec 8.2). */
export function RequestDialog({
  open,
  onOpenChange,
  balances,
  requests,
  editing,
}: RequestDialogProps) {
  // A write in flight keeps the dialog open: its answer decides what the next open shows.
  const busy = useIsMutating({ mutationKey: ptoWriteKeys.request() }) > 0;
  const change = guardClose(busy, onOpenChange);
  const title = editing ? "Edit request" : "Request time off";
  const onDone = () => onOpenChange(false);
  const form: ReactNode = editing ? (
    <RequestForm
      key={editing.id}
      balances={balances}
      requests={requests}
      editing={editing}
      newRequestId=""
      onFresh={onDone}
      onDone={onDone}
    />
  ) : (
    <NewRequestForm balances={balances} requests={requests} onDone={onDone} />
  );
  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Choose the type and dates, then send the request to your approver.
          </DialogDescription>
        </DialogHeader>
        {form}
      </DialogContent>
    </Dialog>
  );
}
